import React from 'react';
import { Box, Typography, Button, Stack, Paper, Chip, useTheme } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { useNavigate } from 'react-router';
import { useFeature } from '../../hooks/useFeature';
import { FeatureKey } from '../../constants/featureKeys';
import { IFeatureAccess } from '../../interfaces/ISubscription';
import { paths } from '../../routes/paths';

interface FeatureLockedContentProps {
    featureKey?: FeatureKey;
    feature?: IFeatureAccess;
    title?: string;
    description?: string;
    suggestedTier?: string;
    showUpgradeButton?: boolean;
    variant?: 'default' | 'compact' | 'minimal';
}

/**
 * Component to display when a feature is locked/unavailable.
 * Handles both "Coming Soon" and "Upgrade Required" states.
 * 
 * @example
 * // Using feature key
 * {bundlesFeature.enabled ? (
 *   <BundlesContent />
 * ) : (
 *   <FeatureLockedContent featureKey={FeatureKeys.TicketBundlesEnabled} />
 * )}
 * 
 * // Using feature object directly
 * <FeatureLockedContent feature={myFeature} variant="compact" />
 * 
 * // Compact variant for smaller spaces
 * <FeatureLockedContent 
 *   featureKey={FeatureKeys.TaskManagementEnabled}
 *   variant="compact"
 * />
 */
export const FeatureLockedContent: React.FC<FeatureLockedContentProps> = ({
    featureKey,
    feature: passedFeature,
    title,
    description,
    suggestedTier = 'Professional',
    showUpgradeButton = true,
    variant = 'default'
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const queriedFeatureResult = featureKey ? useFeature(featureKey) : null;
    const feature = passedFeature || queriedFeatureResult?.feature;

    // Determine if this is a "Coming Soon" feature
    const isComingSoon = feature?.isComingSoon ?? false;
    const isOverridden = feature?.isOverridden ?? false;

    // Get feature display name from the feature key or title
    const getFeatureName = () => {
        if (title) return title;
        if (feature?.name) return feature.name;

        if (featureKey) {
            // Convert feature key to readable name
            // e.g., "TicketBundlesEnabled" -> "Ticket Bundles"
            return featureKey
                .replace(/Enabled$/, '')
                .replace(/([A-Z])/g, ' $1')
                .trim();
        }

        return 'Feature';
    };

    const featureName = getFeatureName();
    const getDescription = () => description || feature?.description;

    const handleUpgradeClick = () => {
        navigate('/manage/company/subscription');
    };

    // Minimal variant - just text and icon
    if (variant === 'minimal') {
        const icon = isComingSoon ? <NewspaperIcon fontSize="small" color="action" /> : <LockOutlinedIcon fontSize="small" color="action" />;
        const message = isComingSoon
            ? `${featureName} is coming soon.`
            : `${featureName} is not available on your current plan.`;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.secondary',
                    py: 1,
                }}
            >
                {icon}
                <Typography variant="body2" color="text.secondary">
                    {message}
                </Typography>
            </Box>
        );
    }

    // Compact variant - for smaller spaces like dialogs
    if (variant === 'compact') {
        const icon = isComingSoon ? <NewspaperIcon /> : <LockOutlinedIcon />;
        const severity = isComingSoon ? 'info' : 'warning';
        const chipLabel = isComingSoon ? 'Coming Soon' : 'Requires Upgrade';

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: isComingSoon
                        ? `${theme.palette.info.main}0f`
                        : `${theme.palette.warning.main}0f`,
                    borderRadius: 1.25,
                    border: `1px dashed ${theme.palette.divider}`,
                }}
            >
                <Box
                    sx={{
                        fontSize: 48,
                        color: isComingSoon ? 'info.main' : 'warning.main',
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                    {featureName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {getDescription() || (isComingSoon
                        ? `We're working on ${featureName.toLowerCase()}. Check back soon!`
                        : `This feature is not available on your current plan.`)}
                </Typography>
                {!isComingSoon && showUpgradeButton && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<RocketLaunchIcon />}
                        onClick={handleUpgradeClick}
                        sx={{ mt: 2, borderRadius: theme.shape.borderRadius }}
                    >
                        Upgrade to {suggestedTier}
                    </Button>
                )}
            </Paper>
        );
    }

    // Default variant - full featured
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                width: '100%',
                p: { xs: 2, md: 3 },
                bgcolor: 'background.default',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    maxWidth: 520,
                    width: '100%',
                    p: { xs: 3, md: 4 },
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    border: `1px dashed ${theme.palette.divider}`,
                }}
            >
                {isComingSoon ? (
                    <>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: theme.palette.info.light + '1f',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                            }}
                        >
                            <NewspaperIcon
                                sx={{
                                    fontSize: 40,
                                    color: 'info.main',
                                }}
                            />
                        </Box>

                        <Typography variant="h5" fontWeight={600} gutterBottom>
                            {featureName} Coming Soon
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {getDescription() || `We're excited to bring ${featureName.toLowerCase()} to you.`}
                        </Typography>

                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                            <Chip label="Coming Soon" color="info" size="small" />
                            {feature?.isBeta && <Chip label="Beta" color="warning" size="small" />}
                        </Stack>

                        <Box sx={{ bgcolor: theme.palette.background.default, p: 2, borderRadius: theme.shape.borderRadius, mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>What to expect:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                * Early access for Professional plans
                                <br />
                                * Full release in upcoming updates
                                <br />
                                * Free during beta testing
                                <br />
                                * Full support from our team
                            </Typography>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            Be among the first to know when this launches. Check back soon!
                        </Typography>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.light + '1f',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                            }}
                        >
                            <LockOutlinedIcon
                                sx={{
                                    fontSize: 40,
                                    color: 'primary.main',
                                }}
                            />
                        </Box>

                        <Typography variant="h5" fontWeight={600} gutterBottom>
                            {featureName}
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {getDescription() || `This feature is not available on your current plan.`}
                        </Typography>

                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                            <Chip label="Requires Upgrade" color="warning" size="small" />
                            <Chip label={`${suggestedTier} Tier`} color="primary" size="small" />
                        </Stack>

                        <Box sx={{ bgcolor: theme.palette.background.default, p: 2, borderRadius: theme.shape.borderRadius, mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Unlock with {suggestedTier}:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                * Full access to {featureName.toLowerCase()}
                                <br />
                                * Lower platform fees
                                <br />
                                * Priority support
                                <br />
                                * Advanced analytics
                            </Typography>
                        </Box>

                        {showUpgradeButton && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<RocketLaunchIcon />}
                                onClick={handleUpgradeClick}
                                fullWidth
                                sx={{
                                    borderRadius: theme.shape.borderRadius,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    py: 1.25,
                                }}
                            >
                                Upgrade to {suggestedTier} 
                            </Button>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            Questions? Contact our sales team for a custom plan.
                        </Typography>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default FeatureLockedContent;
